import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { Button, Stack } from '@optimus-web/core';
import { createZodFormResolver, FormInputField, LocalizedText, Form } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { GeneralArea, useRepositoryMutation } from '@mews-commander/data-access';
import { OverlayedLoader } from '@mews-commander/ui';

const schema = z.object({
    email: z.string().email(),
});

type ResetPasswordFormValues = z.infer<typeof schema>;

const resetPasswordResolver = createZodFormResolver(schema);

export const ResetPasswordForm = ({ onFormSuccess }: { onFormSuccess: (email: string) => void }) => {
    const { mutate, isPending } = useRepositoryMutation('generalUsers', 'resetPassword');

    const onSubmit = ({ email }: ResetPasswordFormValues) => {
        mutate({ email, application: GeneralArea.Commander }, { onSuccess: () => onFormSuccess(email) });
    };

    return (
        <>
            <OverlayedLoader show={isPending} />
            <Form onSubmit={onSubmit} resolver={resetPasswordResolver}>
                <Stack vertical spacing="200">
                    <AutofocusedEmailField />
                    <Button type="submit" variant="primary" wide disabled={isPending}>
                        <LocalizedText textKey={TextKey.Continue} />
                    </Button>
                </Stack>
            </Form>
        </>
    );
};

const AutofocusedEmailField = () => {
    const emailRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        emailRef.current?.focus();
    }, []);

    return (
        <FormInputField
            label={<LocalizedText textKey={TextKey.EmailAddress} />}
            type="email"
            name="email"
            forwardedRef={emailRef}
        />
    );
};
